import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { subscribeAdviserFullDetailsByEmailOrId } from "../../../../context/Adviser/Utils/AdviserLiveDetails";
import Sidebar from "../../../DesignSystem/Sidebar";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import AlertBanner from "../../../DesignSystem/AlertBanner/AlertBanner";
import Field from "../../../DesignSystem/Inputs/Field";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";
import { useUserPermissions } from "../../../SignIn/UserPermissionsContext";
import { PostCard } from "../HelperFunctions/HelperFunctions";
import {
  saveIODetails,
  subscribeIODetails,
} from "./ioDetailsService";
import {
  formatIdentifierList,
  parseIdentifierList,
} from "./ioIdentifierFormatters";
import IOEditLogs from "./IOEditLogs";
import "../../../../css/Adviser/EditAdviserDetailsPage.css";

export default function EditIODetailsPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { userPermissions } = useUserPermissions();
  const adviserKey = decodeURIComponent(params.adviserKey || "");
  const [details, setDetails] = useState(state?.details || null);
  const [ioAdviserId, setIoAdviserId] = useState(
    String(state?.ioDetails?.ioAdviserId ?? ""),
  );
  const [id, setId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [userId, setUserId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const canEdit = ["IO Admin", "Administrator"].some((permission) =>
    userPermissions.includes(permission),
  );
  const canViewEditLogs = userPermissions.some(
    (permission) =>
      String(permission || "").trim().toLowerCase() === "administrator",
  );

  useEffect(() => {
    if (!adviserKey) return undefined;
    return subscribeAdviserFullDetailsByEmailOrId(adviserKey, setDetails);
  }, [adviserKey]);

  const adviserEmail = useMemo(
    () =>
      details?.adviserDoc?.email ||
      details?.email ||
      state?.adviserEmail ||
      adviserKey,
    [adviserKey, details, state?.adviserEmail],
  );

  useEffect(() => {
    if (!adviserEmail) return undefined;

    return subscribeIODetails(
      adviserEmail,
      (ioDetails) => {
        setIoAdviserId(formatIdentifierList(ioDetails?.ioAdviserId));
        setId(formatIdentifierList(ioDetails?.id));
        setGroupId(formatIdentifierList(ioDetails?.groupId));
        setUserId(formatIdentifierList(ioDetails?.userId));
      },
      () => setError("IO details could not be loaded."),
    );
  }, [adviserEmail]);

  const handleSave = async () => {
    if (!canEdit) return;

    try {
      setSaving(true);
      setError("");

      const formattedDetails = {
        ioAdviserId: parseIdentifierList(ioAdviserId, "CRM Contact ID"),
        id: parseIdentifierList(id, "ID"),
        groupId: parseIdentifierList(groupId, "Group ID"),
        userId: parseIdentifierList(userId, "User ID"),
      };
      setIoAdviserId(formattedDetails.ioAdviserId.join(", "));
      setId(formattedDetails.id.join(", "));
      setGroupId(formattedDetails.groupId.join(", "));
      setUserId(formattedDetails.userId.join(", "));
      await saveIODetails(adviserEmail, formattedDetails);

      navigate(
        state?.returnTo ||
          `/advisers/${encodeURIComponent(adviserKey || adviserEmail)}?tab=ioDetails`,
        {
          state: {
            activeTab: "ioDetails",
            successMessage: "IO and ID details saved successfully.",
          },
        },
      );
    } catch (saveError) {
      console.error("Failed to save IO and ID details:", saveError);
      setError(saveError.message || "Failed to save IO and ID details.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="main-content">
      <Helmet>
        <title>Edit IO Details</title>
      </Helmet>
      <Sidebar />
      <div className="Main-Container">
        <div className="smProfilePage">
          {!canEdit ? (
            <AlertBanner
              type="error"
              message="You need the IO Admin or Administrator permission to edit IO details."
            />
          ) : (
            <div className="smFeed">
              <Breadcrumb
                items={[
                  { label: "Advisers", to: "/advisers" },
                  {
                    label: adviserEmail || "Adviser",
                    to: `/advisers/${encodeURIComponent(adviserKey || adviserEmail)}?tab=ioDetails`,
                  },
                  { label: "Edit IO Details" },
                ]}
              />

              {error ? (
                <AlertBanner
                  type="error"
                  message={error}
                  onClose={() => setError("")}
                />
              ) : null}

              <div className="smEditActions">
                <div className="smEditActionsLeft">
                  <h3 className="FP_H3">Edit IO Details</h3>
                </div>
                <PrimaryButton
                  label={saving ? "Saving..." : "Save Details"}
                  onClick={handleSave}
                  disabled={saving}
                />
              </div>

              <div className="FP_Subtitle_Semibold">IO Details</div>
              <AlertBanner
                type="info"
                message="All identifier fields are optional and can be updated independently. Separate multiple values in the same field with commas. Example: 151604, 148536, 148535."
                showClose={false}
              />
              {/* <AlertBanner
                type="warning"
                message="When entering multiple identifiers, separate each value with a comma."
                showClose={false}
              /> */}
              <PostCard title="Adviser identifiers">
                <div className="smGrid2">
                  <Field
                    label="CRM Contact ID"
                    type="text"
                    value={ioAdviserId}
                    onChange={setIoAdviserId}
                    placeholder="Enter IDs separated by commas"
                    tooltip="Enter whole-number identifiers separated by commas. Example: 40894482, 40894544."
                    disabled={saving}
                  />
                  <Field
                    label="ID"
                    type="text"
                    value={id}
                    onChange={setId}
                    placeholder="Enter IDs separated by commas"
                    tooltip="Enter whole-number identifiers separated by commas. Example: 151604, 148536."
                    disabled={saving}
                  />
                  <Field
                    label="Group ID"
                    type="text"
                    value={groupId}
                    onChange={setGroupId}
                    placeholder="Enter group IDs separated by commas"
                    tooltip="Enter whole-number identifiers separated by commas. Example: 36549, 36550."
                    disabled={saving}
                  />
                  <Field
                    label="User ID"
                    type="text"
                    value={userId}
                    onChange={setUserId}
                    placeholder="Enter user IDs separated by commas"
                    tooltip="Enter whole-number identifiers separated by commas. Example: 964277, 917593."
                    disabled={saving}
                  />
                </div>
              </PostCard>
              {canViewEditLogs ? (
                <IOEditLogs adviserEmail={adviserEmail} />
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
